// Master Apps · FiltroDropdown — filtro de MULTISSELEÇÃO em dropdown, com a
// quantidade de cada opção ao lado, busca quando a lista é longa e gatilho que
// resume a escolha ("Área solicitante · 3").
//
// Nasceu para a barra de filtros do lobby da apresentação, onde cinco grupos de
// pílulas empilhados ocupavam a tela inteira antes de a lista de plataformas
// aparecer. Em dropdown os cinco cabem NUMA LINHA e o que sobra de tela é da
// tabela — que é o que se veio olhar.
//
// Não conhece domínio nenhum: recebe `FiltroOpcao[]` com `count` já calculado
// (o chamador traz os contadores facetados) e devolve `onToggle(id)`.
//
// O painel vai para um PORTAL com `position: fixed`, mesma técnica do
// `SelectMenu` e do `MultiSelect`: medido pelo retângulo do gatilho, abre para
// baixo, encosta na borda direita quando não cabe e sobe quando falta altura.
// Fecha por clique fora, Esc e Tab para fora; o foco entra no painel ao abrir
// (na busca, se houver; senão na primeira opção) e volta ao gatilho ao fechar
// por teclado.
//
// `GatilhoFiltro` e `PainelFlutuante` são exportados à parte porque o filtro
// de PERÍODO do lobby tem um conteúdo próprio (chips de ano + De/Até) e
// precisa do mesmo gatilho e do mesmo painel, sem a lista de opções.
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { IconCheck, IconChevronD, IconSearch, IconX } from '@/components/icons';
import { fontSans, makePalette, numeroTabular } from '@/lib/theme';

/** Respiro mínimo entre o painel e a borda da janela. */
const EDGE = 8;
/** Vão entre o gatilho e o painel. */
const GAP = 4;
/** Altura desejada do painel; o espaço real na tela pode reduzi-la. */
const DESIRED_H = 340;
/** Abaixo disto o painel prefere SUBIR a se espremer embaixo do gatilho. */
const MIN_H = 180;
/** Piso da altura quando nem para cima nem para baixo há espaço decente. */
const FLOOR_H = 140;
/** Larguras do painel de opções. */
const PANEL_MIN_W = 240;
const PANEL_MAX_W = 360;
/** A partir de quantas opções o painel ganha campo de busca. */
export const BUSCA_A_PARTIR_DE = 7;

export interface FiltroOpcao {
  id: string;
  label: string;
  /** Quantas plataformas sobram ao ligar esta opção (contador facetado). */
  count: number;
  /** Ponto colorido antes do rótulo (a cor da situação, por exemplo). */
  color?: string;
}

interface PanelPos {
  top?: number;
  bottom?: number;
  left: number;
  maxHeight: number;
}

function portalHost(): HTMLElement {
  const fs = document.fullscreenElement;
  return fs instanceof HTMLElement ? fs : document.body;
}

/** Tudo que recebe foco de teclado dentro do painel, na ordem do DOM. */
function focaveis(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>('input, button:not([disabled]), [tabindex="0"]'),
  );
}

/**
 * Gatilho dos filtros da barra: rótulo + "· N" quando há seleção. Recebe o
 * `ref` para o painel se posicionar por ele.
 */
export function GatilhoFiltro({
  label,
  resumo,
  ativo,
  open,
  onClick,
  dark,
  accent,
  gatilhoRef,
  controla,
}: Readonly<{
  label: string;
  /** O que aparece depois do rótulo ("3", "2026", "Jan–Jun 2026"). */
  resumo?: string;
  /** Há seleção — a borda e o resumo ganham o acento. */
  ativo: boolean;
  open: boolean;
  onClick: () => void;
  dark: boolean;
  accent: string;
  gatilhoRef: RefObject<HTMLButtonElement | null>;
  /** id do painel (aria-controls). */
  controla?: string;
}>) {
  const p = makePalette(dark);
  const [hover, setHover] = useState(false);
  let borda = p.border;
  if (open || hover) borda = `${accent}88`;
  else if (ativo) borda = `${accent}66`;
  let fundo = p.inputBg;
  if (ativo) fundo = dark ? `${accent}1f` : `${accent}0f`;
  return (
    <button
      ref={gatilhoRef}
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? controla : undefined}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: 32,
        padding: '0 9px 0 11px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 7,
        border: `1px solid ${borda}`,
        background: fundo,
        color: p.text,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: fontSans,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'border-color 80ms ease-out',
        maxWidth: '100%',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {resumo && (
        <span
          style={{
            ...numeroTabular,
            color: ativo ? accent : p.muted,
            fontWeight: 700,
          }}
        >
          · {resumo}
        </span>
      )}
      <span
        style={{
          display: 'inline-flex',
          color: p.muted,
          transition: 'transform 80ms ease-out',
          transform: open ? 'rotate(180deg)' : 'none',
        }}
      >
        <IconChevronD size={12} />
      </span>
    </button>
  );
}

/**
 * Painel ancorado a um gatilho, num portal `fixed`. Fecha por clique fora, Esc
 * e Tab para fora. Não sabe o que há dentro.
 */
export function PainelFlutuante({
  open,
  onClose,
  gatilhoRef,
  dark,
  id,
  label,
  minWidth = PANEL_MIN_W,
  maxWidth = PANEL_MAX_W,
  children,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  gatilhoRef: RefObject<HTMLButtonElement | null>;
  dark: boolean;
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  children: ReactNode;
}>) {
  const p = makePalette(dark);
  const painelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<PanelPos | null>(null);

  /**
   * Posiciona pelo retângulo do gatilho. Alinha à ESQUERDA do gatilho e, se o
   * painel não couber à direita da janela, encosta na borda direita — é o caso
   * do último filtro da barra num notebook.
   */
  const measure = useCallback(() => {
    const g = gatilhoRef.current;
    if (!g) return;
    const r = g.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const below = vh - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const up = below < MIN_H && above > below;
    const w = Math.min(painelRef.current?.offsetWidth ?? minWidth, vw - 2 * EDGE);
    const left = Math.min(Math.max(EDGE, r.left), Math.max(EDGE, vw - w - EDGE));
    const next: PanelPos = {
      top: up ? undefined : r.bottom + GAP,
      bottom: up ? vh - r.top + GAP : undefined,
      left,
      maxHeight: Math.min(DESIRED_H, Math.max(FLOOR_H, Math.floor(up ? above : below))),
    };
    setPos((prev) =>
      prev &&
      prev.top === next.top &&
      prev.bottom === next.bottom &&
      prev.left === next.left &&
      prev.maxHeight === next.maxHeight
        ? prev
        : next,
    );
  }, [gatilhoRef, minWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onReflow = () => measure();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, measure]);

  // Foco entra no painel ao abrir: na busca, se houver, senão no primeiro
  // controle. Sem isto o Tab depois de abrir iria para o filtro seguinte da
  // barra e o painel ficaria aberto sem foco dentro.
  useEffect(() => {
    if (!open || !pos) return;
    const root = painelRef.current;
    if (!root) return;
    const alvo = root.querySelector<HTMLElement>('input') ?? focaveis(root)[0];
    alvo?.focus({ preventScroll: true });
  }, [open, pos]);

  // Clique fora fecha. `mousedown` (não click): fechar antes de o clique do
  // outro controle disparar evita o "clique fantasma" que abre dois painéis.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (gatilhoRef.current?.contains(alvo) || painelRef.current?.contains(alvo)) return;
      // Um `SelectMenu` DENTRO do painel (os De/Até do período) desenha a
      // própria lista noutro portal, fora desta árvore: clicar numa opção dele
      // não é "clicar fora" — sem esta exceção o painel fechava antes do
      // clique chegar à opção e a escolha se perdia.
      if (alvo instanceof Element && alvo.closest('[role="listbox"]')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, gatilhoRef]);

  // Esc fecha e devolve o foco ao gatilho (o painel some de baixo do cursor de
  // teclado e o leitor de tela precisa saber onde está).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
      gatilhoRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, gatilhoRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={painelRef}
      id={id}
      role="dialog"
      aria-label={label}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        // Tab para fora do último (ou Shift+Tab antes do primeiro) fecha e
        // segue o fluxo natural da página a partir do gatilho.
        if (e.key !== 'Tab') return;
        const lista = focaveis(e.currentTarget);
        const primeiro = lista[0];
        const ultimo = lista[lista.length - 1];
        const saiPorBaixo = !e.shiftKey && document.activeElement === ultimo;
        const saiPorCima = e.shiftKey && document.activeElement === primeiro;
        if (saiPorBaixo || saiPorCima) {
          e.preventDefault();
          onClose();
          gatilhoRef.current?.focus();
        }
      }}
      style={{
        position: 'fixed',
        top: pos?.top ?? 0,
        bottom: pos?.bottom,
        left: pos?.left ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        pointerEvents: pos ? 'auto' : 'none',
        zIndex: 8001,
        minWidth,
        maxWidth: `min(${maxWidth}px, calc(100vw - ${2 * EDGE}px))`,
        maxHeight: pos?.maxHeight ?? DESIRED_H,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 9,
        border: `1px solid ${p.border}`,
        background: p.surface,
        boxShadow: '0 12px 32px -8px rgba(0,0,0,0.45)',
        fontFamily: fontSans,
        color: p.text,
      }}
    >
      {children}
    </div>,
    portalHost(),
  );
}

/**
 * Filtro de multisseleção completo: gatilho + painel com busca (a partir de
 * `BUSCA_A_PARTIR_DE` opções), lista com caixinha e contador, e "Limpar".
 */
export function FiltroDropdown({
  label,
  options,
  selected,
  onToggle,
  onClear,
  dark,
  accent,
  emptyLabel = 'Nada para filtrar aqui.',
}: Readonly<{
  label: string;
  options: FiltroOpcao[];
  selected: string[];
  onToggle: (id: string) => void;
  /** Desmarca tudo deste filtro. Sem ele o botão "Limpar" não aparece. */
  onClear?: () => void;
  dark: boolean;
  accent: string;
  emptyLabel?: string;
}>) {
  const p = makePalette(dark);
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [lit, setLit] = useState<string | null>(null);
  const gatilhoRef = useRef<HTMLButtonElement | null>(null);
  const painelId = useId();
  const fechar = useCallback(() => setOpen(false), []);

  const temBusca = options.length >= BUSCA_A_PARTIR_DE;
  const q = busca.trim().toLowerCase();
  const visiveis = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  const abrir = () => {
    setBusca('');
    setLit(null);
    setOpen(true);
  };

  // Setas movem o foco entre as opções (e a busca); Enter/Espaço é o próprio
  // botão. Home/End pulam para as pontas.
  const onKeyDownLista = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const lista = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>('input, [role="option"]'),
    );
    if (lista.length === 0) return;
    const i = lista.indexOf(document.activeElement as HTMLElement);
    let alvo = 0;
    if (e.key === 'ArrowDown') alvo = Math.min(lista.length - 1, i + 1);
    else if (e.key === 'ArrowUp') alvo = Math.max(0, i - 1);
    else if (e.key === 'End') alvo = lista.length - 1;
    e.preventDefault();
    lista[alvo]?.focus();
  };

  const vazioStyle = { padding: '10px 9px', fontSize: 12, color: p.muted } as const;

  return (
    <>
      <GatilhoFiltro
        label={label}
        resumo={selected.length > 0 ? String(selected.length) : undefined}
        ativo={selected.length > 0}
        open={open}
        onClick={() => (open ? fechar() : abrir())}
        dark={dark}
        accent={accent}
        gatilhoRef={gatilhoRef}
        controla={painelId}
      />
      <PainelFlutuante
        open={open}
        onClose={fechar}
        gatilhoRef={gatilhoRef}
        dark={dark}
        id={painelId}
        label={label}
      >
        <div
          onKeyDown={onKeyDownLista}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
        >
          {temBusca && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                height: 34,
                padding: '0 10px',
                borderBottom: `1px solid ${p.border}`,
                color: p.muted,
                flexShrink: 0,
              }}
            >
              <IconSearch size={13} />
              <input
                value={busca}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBusca(e.target.value)}
                placeholder={`Pesquisar em ${label.toLowerCase()}…`}
                aria-label={`Pesquisar em ${label}`}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: 12,
                  color: p.text,
                  fontFamily: fontSans,
                }}
              />
              {busca && (
                <button
                  type="button"
                  aria-label="Limpar pesquisa"
                  onClick={() => setBusca('')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: p.muted,
                    cursor: 'pointer',
                    display: 'flex',
                    padding: 0,
                  }}
                >
                  <IconX size={11} />
                </button>
              )}
            </div>
          )}

          <div
            role="listbox"
            aria-multiselectable
            aria-label={label}
            style={{ overflowY: 'auto', overflowX: 'hidden', padding: 4, flex: 1, minHeight: 0 }}
          >
            {options.length === 0 && <div style={vazioStyle}>{emptyLabel}</div>}
            {options.length > 0 && visiveis.length === 0 && (
              <div style={vazioStyle}>Nada encontrado para “{busca.trim()}”.</div>
            )}
            {visiveis.map((o) => {
              const on = selected.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => onToggle(o.id)}
                  onMouseEnter={() => setLit(o.id)}
                  onMouseLeave={() => setLit((v) => (v === o.id ? null : v))}
                  onFocus={() => setLit(o.id)}
                  style={{
                    appearance: 'none',
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '6px 8px',
                    borderRadius: 6,
                    background: lit === o.id ? p.surface2 : 'transparent',
                    color: on ? p.text : p.textMuted,
                    fontSize: 12,
                    fontWeight: on ? 600 : 500,
                    fontFamily: fontSans,
                    cursor: 'pointer',
                    // Zero disponível continua clicável de propósito: a
                    // faceta descreve o resultado, não proíbe a escolha.
                    opacity: o.count === 0 && !on ? 0.55 : 1,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 15,
                      height: 15,
                      borderRadius: 4,
                      flexShrink: 0,
                      background: on ? accent : 'transparent',
                      border: `1.5px solid ${on ? accent : p.borderStrong}`,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                    }}
                  >
                    {on && <IconCheck size={10} />}
                  </span>
                  {o.color && (
                    <span
                      aria-hidden="true"
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        background: o.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span
                    title={o.label}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {o.label}
                  </span>
                  <span
                    style={{
                      ...numeroTabular,
                      fontSize: 11,
                      fontWeight: 700,
                      color: p.muted,
                      flexShrink: 0,
                    }}
                  >
                    {o.count}
                  </span>
                </button>
              );
            })}
          </div>

          {onClear && selected.length > 0 && (
            <div
              style={{
                borderTop: `1px solid ${p.border}`,
                padding: 6,
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={onClear}
                style={{
                  height: 26,
                  padding: '0 10px',
                  background: 'transparent',
                  border: `1px solid ${p.border}`,
                  borderRadius: 6,
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: p.textMuted,
                  fontFamily: fontSans,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <IconX size={11} /> Limpar
              </button>
            </div>
          )}
        </div>
      </PainelFlutuante>
    </>
  );
}
